import { z } from 'zod';

/**
 * Auto QA reports and search (docs/37 §7, Q3).
 *
 * Reports are arithmetic: marks, passes, sentiment numbers and counts, which staff may see as well
 * as the account (the words are the account's alone, docs/37 §5). Search reads the words, so it is
 * the account's own users only.
 */

const Day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const QA_REPORT_GROUPS = ['agent', 'department', 'queue', 'campaign'] as const;
export type QaReportGroup = (typeof QA_REPORT_GROUPS)[number];

export const QaReportQuerySchema = z.object({
  from: Day.optional(),
  to: Day.optional(),
  group: z.enum(QA_REPORT_GROUPS).default('agent'),
}).strict();
export type QaReportQuery = z.infer<typeof QaReportQuerySchema>;

export interface QaReportFigures {
  /** Calls QA finished. */
  calls: number;
  /** Of them, marked against a scorecard. */
  marked: number;
  /** Average mark out of 100, over marked calls. */
  avg_score: number | null;
  /** Marked calls at or above their card's pass mark and not auto-failed. */
  passed: number;
  auto_failed: number;
  /** Average sentiment, -1..1. */
  sentiment: number | null;
  findings_high: number;
}

export interface QaReport {
  range: { from: string | null; to: string | null; tz: string };
  group: QaReportGroup;
  totals: QaReportFigures;
  /** One row per agent, department, queue or campaign, best average first. */
  rows: Array<QaReportFigures & { key: string; name: string }>;
  /** Every scorecard item that was answered: how often it passed, and how often it failed a call. */
  questions: Array<{
    id: string; scorecard: string; text: string; kind: 'question' | 'phrase'; auto_fail: boolean;
    answered: number; passed: number; overridden: number;
  }>;
  /** By the account's week (Monday). */
  weeks: Array<{ week: string; calls: number; avg_score: number | null; passed: number; marked: number; sentiment: number | null }>;
}

export const QaSearchQuerySchema = z.object({
  q: z.string().trim().min(2).max(200),
  speaker: z.enum(['agent', 'caller', 'either']).default('either'),
  from: Day.optional(),
  to: Day.optional(),
}).strict();

export interface QaSearchHit {
  run_id: string;
  cdr_id: string;
  started_at: string;
  extension_number: string | null;
  party: string | null;
  direction: string | null;
  score: number | null;
  /** How many lines of the call matched. */
  matches: number;
  /** The first few, in call order. */
  lines: Array<{ seq: number; speaker: 'agent' | 'caller'; start_ms: number; text: string }>;
}

export interface QaSearchResult {
  hits: QaSearchHit[];
  /** True when there were more calls than returned. */
  more: boolean;
}

// ============================================================================ CSV

/** One CSV cell: quoted when it needs to be, and never read by a spreadsheet as a formula. */
export function csvCell(v: unknown): string {
  if (v === null || v === undefined) return '';
  let s = typeof v === 'number' ? (Number.isFinite(v) ? String(v) : '') : String(v);
  // A cell starting with = + - @ (or a tab/CR) is a formula to Excel: a transcript line or a lead's
  // name could otherwise run one on a supervisor's machine.
  if (/^[=+\-@\t\r]/.test(s) && !(typeof v === 'number')) s = `'${s}`;
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function toCsv(header: readonly string[], rows: ReadonlyArray<readonly unknown[]>): string {
  return [header, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n') + '\r\n';
}
